import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Download, Home, Calendar, Users, MapPin, Receipt, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import { bookingsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const fmt = (n) => n?.toLocaleString('en-IN') || '0';

const BookingConfirmation = () => {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!bookingId) return;
    bookingsAPI.getById(bookingId)
      .then(setBooking)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !booking) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4">
      <p className="text-gray-500">{error || 'Booking not found'}</p>
      <Link to="/" className="text-gold hover:underline text-sm">Go Home</Link>
    </div>
  );

  const isStay = booking.experience_type === 'stay';

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 pt-28 pb-16">
        {/* Success header */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h1 className="font-serif text-3xl font-black text-navy mb-2">Booking Confirmed!</h1>
          <p className="text-gray-500">Your booking is confirmed and payment has been received.</p>
        </motion.div>

        {/* Receipt card */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          data-testid="booking-receipt"
          className="bg-white rounded-2xl border border-gray-100 overflow-hidden print:shadow-none"
          style={{ boxShadow: '0 4px 24px rgba(10,22,40,0.08)' }}>

          {/* Receipt header */}
          <div className="bg-navy px-8 py-6 flex items-center justify-between">
            <div>
              <p className="text-gold text-xs uppercase tracking-[0.2em] font-medium mb-1">HYGAD Booking Receipt</p>
              <p data-testid="receipt-booking-id" className="font-mono text-white text-xl font-bold">{booking.booking_id}</p>
            </div>
            <div className="text-right">
              <p className="text-white/50 text-xs">Payment Status</p>
              <span className={`text-sm font-semibold px-3 py-1 rounded-full mt-1 inline-block ${
                booking.payment_status === 'paid' ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'
              }`}>
                {booking.payment_status === 'paid' ? 'Paid' : booking.payment_status}
              </span>
            </div>
          </div>

          {/* Property details */}
          <div className="px-8 py-6 border-b border-gray-100">
            <h3 className="font-serif text-xl font-bold text-navy mb-1">{booking.property_name}</h3>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span className="bg-gold/10 text-gold text-xs px-2 py-0.5 rounded-full capitalize">{booking.experience_type}</span>
              <span className="flex items-center gap-1"><Users size={13} />{booking.guests} guest{booking.guests > 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* Dates */}
          <div className="px-8 py-5 border-b border-gray-100">
            <div className="grid grid-cols-2 gap-6">
              {isStay ? (
                <>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Check-in</p>
                    <p className="font-semibold text-navy">{new Date(booking.check_in + 'T12:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Check-out</p>
                    <p className="font-semibold text-navy">{new Date(booking.check_out + 'T12:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Duration</p>
                    <p className="font-semibold text-navy">{booking.nights} night{booking.nights > 1 ? 's' : ''}</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Event Date</p>
                    <p className="font-semibold text-navy">{new Date(booking.event_date + 'T12:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  {booking.event_time && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Event Time</p>
                      <p className="font-semibold text-navy">{booking.event_time}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Customer info */}
          <div className="px-8 py-5 border-b border-gray-100">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Guest Details</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-gray-400 text-xs">Name</p><p className="font-medium text-navy">{booking.customer_name}</p></div>
              <div><p className="text-gray-400 text-xs">Phone</p><p className="font-medium text-navy">{booking.customer_phone}</p></div>
              <div className="col-span-2"><p className="text-gray-400 text-xs">Email</p><p className="font-medium text-navy">{booking.customer_email}</p></div>
            </div>
          </div>

          {/* Pricing breakdown */}
          <div className="px-8 py-5 border-b border-gray-100">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Payment Summary</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>₹{fmt(booking.price_per_night)} × {booking.nights} {isStay ? 'night' : 'day'}{booking.nights > 1 ? 's' : ''}</span>
                <span>₹{fmt(booking.subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>GST ({booking.tax_rate}%)</span>
                <span>₹{fmt(booking.tax_amount)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Convenience fee ({booking.convenience_fee_rate}%)</span>
                <span>₹{fmt(booking.convenience_fee)}</span>
              </div>
              <div className="flex justify-between font-bold text-navy text-lg pt-2 border-t border-gray-200">
                <span>Total Paid</span>
                <span className="text-gold">₹{fmt(booking.total)}</span>
              </div>
            </div>
          </div>

          {/* Payment reference */}
          <div className="px-8 py-4 bg-[#FAFAF9]">
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>Payment ID: {booking.razorpay_payment_id || 'Pending'}</span>
              <span>Booked: {new Date(booking.created_at).toLocaleDateString('en-IN')}</span>
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-3 mt-6">
          <button onClick={handlePrint}
            className="flex-1 flex items-center justify-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white py-3 rounded-xl text-sm font-semibold transition-colors">
            <Download size={15} /> Download / Print Receipt
          </button>
          <Link to="/customer"
            className="flex-1 flex items-center justify-center gap-2 bg-navy text-white hover:bg-navy-light py-3 rounded-xl text-sm font-semibold transition-colors">
            <Receipt size={15} /> My Bookings
          </Link>
          <Link to="/"
            className="flex items-center justify-center gap-2 text-gray-400 hover:text-navy py-3 px-4 rounded-xl text-sm transition-colors">
            <Home size={15} /> Home
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
